import { Link, useNavigate } from 'react-router';
import { clsx } from '../../ds/clsx';
import { Banner, Button, Skeleton, useToast } from '../../ds/components';
import { errorMessage } from '../../lib/api';
import { usePermissions, useWorkspace } from '../../lib/session';
import { useEventTypes, type EventType } from './api';
import styles from './EventTypesPage.module.css';

/** Roles that only read event types (docs/dev/api-appointments.md permission rule). */
const READ_ONLY_ROLES: ReadonlySet<string> = new Set(['setter', 'back_office', 'financier', 'referrer']);

export function canCreateEventTypes(roleKey: string, hasOrganizationSettings: boolean): boolean {
  return hasOrganizationSettings || !READ_ONLY_ROLES.has(roleKey);
}

/** Absolute booking URL of a type (`/book/<org>/<slug>` on this origin). */
export function absoluteBookingUrl(bookingUrl: string): string {
  return `${window.location.origin}${bookingUrl}`;
}

/** "brimo.de/hallmayr/erstberatung" — the host-relative link text of the screens. */
export function bookingLinkText(bookingUrl: string): string {
  return `${window.location.host}${bookingUrl}`;
}

/** The colour bar of a card: the type's token, else accent (active) / quiet (inactive). */
export function colorBar(type: Pick<EventType, 'colorToken' | 'active'>): string {
  if (!type.active) return 'var(--surface-quiet)';
  if (type.colorToken && /^[a-z0-9-]+$/.test(type.colorToken))
    return `var(--${type.colorToken}, var(--accent))`;
  return 'var(--accent)';
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function tagClass(tag: string): string | undefined {
  if (tag === 'Aktiv') return styles.tagActive;
  if (tag === 'Eigener Ablauf') return styles.tagAccent;
  return undefined;
}

function EventTypeCard({ type }: { type: EventType }) {
  const { toast } = useToast();
  const slugText = `/${type.slug}`;
  async function copy() {
    if (!type.bookingUrl) return;
    const ok = await copyText(absoluteBookingUrl(type.bookingUrl));
    toast({ message: ok ? 'Link kopiert.' : 'Der Link konnte nicht kopiert werden.' });
  }
  return (
    <article className={clsx(styles.card, !type.active && styles.cardInactive)}>
      <div className={styles.bar} style={{ background: colorBar(type) }} aria-hidden="true" />
      <div className={styles.cardBody}>
        <h3 className={styles.cardName}>
          <Link to={`/calendar/event-types/${type.id}`} className={styles.cardLink}>
            {type.name}
          </Link>
        </h3>
        <div className={styles.cardSummary}>{type.summary}</div>
        <div className={styles.tags}>
          {type.tags.map((tag) => (
            <span key={tag} className={clsx(styles.tag, tagClass(tag))}>
              {tag}
            </span>
          ))}
        </div>
      </div>
      <div className={styles.cardFoot}>
        <span className={styles.slug}>{slugText}</span>
        {type.bookingUrl ? (
          <button type="button" className={styles.copy} onClick={() => void copy()}>
            Link kopieren
          </button>
        ) : (
          <span className={styles.noLink}>Kein Link</span>
        )}
      </div>
    </article>
  );
}

function CardSkeleton() {
  return (
    <div className={styles.card} aria-hidden="true">
      <div className={styles.bar} style={{ background: 'var(--surface-quiet)' }} />
      <div className={styles.cardBody}>
        <Skeleton width={140} height={18} />
        <Skeleton width={200} style={{ marginTop: 8 }} />
        <Skeleton width={90} style={{ marginTop: 12 }} />
      </div>
      <div className={styles.cardFoot}>
        <Skeleton width={80} />
      </div>
    </div>
  );
}

/** Beratung 05 — the card grid of the event types with their booking links. */
export function EventTypesPage() {
  const navigate = useNavigate();
  const workspace = useWorkspace();
  const permissions = usePermissions();
  const types = useEventTypes();
  const mayCreate = canCreateEventTypes(workspace.user.roleKey, permissions.has('settings.organization'));
  const base = `${window.location.host}/book/${workspace.organizationSlug}/…`;

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <div>
          <h2 className={styles.title}>Ereignistypen</h2>
          <div className={styles.subtitle}>Buchungslinks unter {base}</div>
        </div>
        <span className={styles.headActions}>
          {permissions.is('settings.organization', 'yes') ? (
            <Button
              variant="secondary"
              size="small"
              className={styles.newButton}
              onClick={() => navigate('/calendar/reminders')}
            >
              Ablauf · Org-Standard
            </Button>
          ) : null}
          {mayCreate ? (
            <Button
              size="small"
              className={styles.newButton}
              onClick={() => navigate('/calendar/event-types/new')}
            >
              + Neuer Ereignistyp
            </Button>
          ) : null}
        </span>
      </div>
      {types.isError ? (
        <div className={styles.banner}>
          <Banner
            tone="danger"
            actions={
              <Button variant="secondary" size="small" onClick={() => void types.refetch()}>
                Erneut laden
              </Button>
            }
          >
            {errorMessage(types.error)}
          </Banner>
        </div>
      ) : (
        <div className={styles.grid} aria-busy={types.isPending || undefined}>
          {types.isPending
            ? [0, 1, 2, 3].map((index) => <CardSkeleton key={index} />)
            : types.data.map((type) => <EventTypeCard key={type.id} type={type} />)}
        </div>
      )}
    </div>
  );
}
